export default {
  settings: {
    disableIframePostMessage: false,
  },

  lastUpdated: 'July 2026',
  revision: '2',
  analytics: {
    // gtmcode:"GTM-TJBC8K3M",
    gtmcode:"GTM-NCL4Z8",
    gacode: "G-KQG4N84HK1"
  },

  content: {
    title: "Prime Super | Hitting the target",
    welcome: {
      logoUrl: "primesuper-logo.svg",
      content: `
        <h2 class="lt-welcome-title" tabindex="0">Hitting the Target</h2>
        <p>Build a picture of your lifestyle in retirement by taking this quick quiz.</p>
        <p>Then, find out how much money you’d need each year to achieve it.</p>`,
      buttonLabel: "Start building your future",
    },

    results: {
      subheader: "Based on your chosen lifestyle, and assuming you own your own home, we have estimated you'll need:",
      supermodellerName: "retirement projection calculator",
      helpText: `<p class="m-0">The AFSA retirement standard calculates the annual expenditure in today’s dollars to maintain a basic, modest and comfortable lifestyle in retirement. The amount assumes that the retiree(s) own their own home outright and are relatively healthy. Learn more about the <a href='https://www.superannuation.asn.au/resources/retirement-standard' target='_blank' >ASFA Retirement standard and underlying assumptions.</a></p>`,
      CTA1: `
      <h3>Want to learn how you can contribute extra to super?</h3>
      <p><a href='https://primesuper.com.au/member/super/grow-your-super' target='_blank'>Adding extra to your super</a> while you're working can help boost your income when you retire, and may also reduce the tax you pay.</p>
      `,
      CTA2: `
      <h3>Understand your retirement options</h3>
      <p>We've put together some examples to help you <a href='https://www.primesuper.com.au/member/retirement/thinking-about-retirement/case-studies' target='_blank'>understand your options</a> as you plan the retirement that's right for you.</p>
      `,
      CTA3: `
      <h3>We're here to help</h3>
      <p>Retirement is different for everyone. For confidence planning your retirement, <a href='https://primesuper.com.au/member/book-a-chat' target='_blank'>book a chat with our super specialists.</a></p>
      `,
    },

    standards: {
      minimum: {
        heading: "Learn more about the ASFA Retirement Standard: Age Pension",
        headingHousehold: "Household expenses",
        descriptionHousehold: "Very basic repairs, like leaky roofs or major plumbing problem",
        headingTravel: "Travel",
        descriptionTravel: "Occasional short break or day trip in your own city",
        headingFood: "Food",
        descriptionFood: "Only local club special meals or inexpensive take-away",
        headingClothes: "Clothes",
        descriptionClothes: "Very basic clothing and footwear budget"
      },
      modest: {
        heading: "Learn more about the ASFA Retirement Standard: Modest",
        headingHousehold: "Household expenses",
        descriptionHousehold: "Limited budget for home repairs and household appliances",
        headingTravel: "Travel",
        descriptionTravel: "Annual domestic trip or a few short breaks",
        headingFood: "Food",
        descriptionFood: "Limited meals out at inexpensive restaurants, infrequent home-delivery or take-away",
        headingClothes: "Clothes",
        descriptionClothes: "Limited budget to replace or update worn items"
      },
      comfortable: {
        heading: "Learn more about the ASFA Retirement Standard: Comfortable",
        headingHousehold: "Household expenses",
        descriptionHousehold: "Home repairs, updates and maintenance to kitchen and bathroom appliances",
        headingTravel: "Travel",
        descriptionTravel: "Annual domestic trip to visit family, one overseas trip every seven years",
        headingFood: "Food",
        descriptionFood: "Occasional restaurant meals, home-delivery meals, take-away coffee",
        headingClothes: "Clothes",
        descriptionClothes: "Replace worn-out clothing and footwear items, modest wardrobe updates"
      }
    },

    disclaimer: {
      content: `
      <h4>Disclaimer</h4>
      <p>This calculator has been developed by Gallagher Benefit Services Pty Limited trading as Gallagher Digital Engagement.</p>
      <p>This calculator provides an illustration of your estimate retirement income. The totals are calculated based on the data you input and can only be as accurate as the information provided.</p>
      <p>Please remember that the results shown by the modeller are for illustrative purposes only and do not guarantee any future outcome or entitlement.</p>
      <p>You should also remember that you will need to pay income tax and the retirement target figure is based on your expenditure, not your pre-tax income.</p>
      <p>Any choices you make or data that you enter while using the calculator are erased once you have finished using the calculator. However, you have a responsibility to ensure that you exit the website when you have finished with the calculator, especially if you leave your computer for any length of time.</p>
      <h4>Assumptions</h4>
      <p>Your estimate retirement income calculation is based on research conducted by the Association of Superannuation Funds of Australia (ASFA). The research sets out three ‘Retirement Living Standards’ (Minimum, Moderate and Comfortable) which are based upon a basket of goods and services. You can find out more about the research and each of the Standards at <a href='https://www.superannuation.asn.au/resources/retirement-standard' target='_blank'>https://www.superannuation.asn.au/resources/retirement-standard</a></p>
      <p>Your estimated retirement income figure assumes you will have no rent or mortgage costs to pay in retirement. If you have rent or mortgage costs to pay in retirement, you may wish to consider how this may impact your target retirement income.</p>
      <p>The estimate provided by this calculator is based on the following assumptions:</p>
      <ul>
        <li>If you selected that you live with a partner, this is your total ‘household’ savings amount.</li>
        <li>It will provide you with an estimated retirement income based on <a href='https://www.superannuation.asn.au/resources/retirement-standard' target='_blank'>ASFA’s Retirement Standards</a> Detailed Expenditure Breakdowns. The figures in each case assume that the retiree(s) own their own home and relate to expenditure by the household. This can be greater than household income after income tax where there is a drawdown on capital over the period of retirement.</li>
        <li>The “Age Pension” figure is based on the full government age pension. It does not determine your eligibility or entitlements for the age pension. You can find out more about the age pension at <a href='https://www.servicesaustralia.gov.au/who-can-get-age-pension?context=22526' target='_blank'>www.servicesaustralia.gov.au/who-can-get-age-pension</a>.</li>
        <li>All figures and current tax rates are in today’s terms to take into account the effect of inflation.</li>
      </ul>
      `,
    },

    assumptions: {
      content: ``,
    },

    gtag: {
      clickCTA1Description: `Get advice`,
      clickCTA2Description: `Attend a seminar`,
      clickCTA3Description: `Start planning for retirement`
    }
  },

  params: {
    ageNow: {
      min: 18,
      max: 74,
    },
    salary: {
      min: 100,
      max: 500000,
    },
    superannuationBalance: {
      min: 0,
      max: 3000000,
    },
    retirementAge: {
      min: 55,
      max: 74,
    },
  },
};
